// Data layer for the General Report page.
//
// The page NEVER calls axios directly — it always goes through fetchSection().
// That keeps one seam between "where the numbers come from" and "how they're
// drawn", so the components stay unaware of transport and response shape.
//
// Server contract — see GENERAL_REPORT_PLAN.md §10. All three endpoints return
// the SAME envelope, which is the whole point:
//
//   { error: 0, message: "", startDate, endDate,
//     series: [ { key, label, total, points: [{ date, count }] } ] }
//
// scans-by-passtype additionally carries a `type` per row and needs folding into
// three buckets (aggregatePassTypes); the other two pass straight through.
import axios from 'axios';
import { MAX_SERIES, OTHER_KEY, OTHER_LABEL } from '../Components/charts/vizPalette';
import { formatDayLabel } from '../Components/charts/chartFormat';

// The three pass types shown by the report.
//
// The `type` field on the scans-by-passtype response is `promotions.type`, and
// carries the same meaning it does everywhere else in the app:
//
//        type   promotions.type (DB comment, PromotionForm.js:678-681)
//        0      Membership
//        1      Public promotion  -> shown as "Branded Coupon Pass"
//        2      Member offer      -> shown as "Attached-Coupon Pass"
//
// Only the display names differ from the promotion form's labels; the numbers
// mean the same thing. Behaviourally this matches the pass model too: a type-1
// promotion gets its OWN pass (hence "branded"), while a type-2 promotion rides
// on the member's existing membership pass (hence "attached").
//
// The array order below is the display order — KPI tiles, legend, and the seeded
// colour slots in GeneralReport.js all follow it. It is not the numeric order.
export const PASS_TYPES = [
    { key: 'membership', label: 'Membership Pass', reportType: 0 },
    { key: 'attached', label: 'Attached-Coupon Pass', reportType: 2 },
    { key: 'branded', label: 'Branded Coupon Pass', reportType: 1 },
];

// report `type` -> our series key. Built from PASS_TYPES so the two can't drift.
const REPORT_TYPE_TO_KEY = PASS_TYPES.reduce((acc, t) => {
    acc[t.reportType] = t.key;
    return acc;
}, {});

export const SECTIONS = {
    passType: {
        id: 'passType',
        title: 'Scans per day by pass type',
        urlMethod: 'generalScansByPassTypeUrl',
        entityLabel: 'Pass type',
    },
    // Location and promotion show scan counts only — no pass-type breakdown and
    // no membership filter. Splitting location by pass type is a later phase, so
    // the titles deliberately say "Scans", not "Membership scans".
    location: {
        id: 'location',
        title: 'Scans per day by location',
        urlMethod: 'generalScansByLocationUrl',
        entityLabel: 'Location',
    },
    promotion: {
        // Server returns coupon promotions only (report types 1 & 2), labelled
        // with the promotion name. Not surfaced in the title — see above.
        id: 'promotion',
        title: 'Scans per day by promotion',
        urlMethod: 'generalScansByPromotionUrl',
        entityLabel: 'Promotion',
    },
};

// A range longer than this is almost certainly a mis-set date rather than a real
// request; enumerating every day would freeze the chart.
const MAX_DAYS = 400;

// ---------------------------------------------------------------------------
// Date helpers. All arithmetic is done in UTC on "YYYY-MM-DD" strings so a
// browser timezone can never shift a day bucket.
// ---------------------------------------------------------------------------

function parseYmd(s) {
    const [y, m, d] = String(s).split('-').map(Number);
    return Date.UTC(y, m - 1, d);
}

function toYmd(ms) {
    const dt = new Date(ms);
    const y = dt.getUTCFullYear();
    const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
    const d = String(dt.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

// Every date in [startDate, endDate] inclusive.
export function enumerateDays(startDate, endDate) {
    if (!startDate || !endDate) return [];
    const start = parseYmd(startDate);
    const end = parseYmd(endDate);
    if (Number.isNaN(start) || Number.isNaN(end) || end < start) return [];
    const days = [];
    const DAY = 86400000;
    for (let t = start; t <= end && days.length < MAX_DAYS; t += DAY) {
        days.push(toYmd(t));
    }
    return days;
}

// Re-exported for convenience: charts get the raw ISO date as the data key and
// only format at display time, so sorting stays lexicographic and correct.
export { formatDayLabel };

// ---------------------------------------------------------------------------
// Normalisation
// ---------------------------------------------------------------------------

// Give every series a value on every day in the window. Without this, a gap in
// the data reads as a dip that never happened.
export function zeroFill(series, days) {
    const inRange = new Set(days);
    return (series || []).map(s => {
        const byDate = {};
        let dropped = 0;

        (s.points || []).forEach(p => {
            if (!inRange.has(p.date)) { dropped += 1; return; }
            // SUM, don't overwrite. The server may return several rows for the
            // same date when its query groups by an inner dimension as well as
            // by day; assigning here would silently keep only the last row.
            byDate[p.date] = (byDate[p.date] || 0) + (Number(p.count) || 0);
        });

        if (dropped > 0) {
            console.warn(
                `[GeneralReport] series "${s.label || s.key}": ${dropped} point(s) fell `
                + `outside the requested range and were ignored.`,
            );
        }

        const points = days.map(date => ({ date, count: byDate[date] || 0 }));
        const total = points.reduce((sum, p) => sum + p.count, 0);
        return { key: String(s.key), label: s.label || String(s.key), total, points };
    });
}

/**
 * Collapse the scans-by-passtype response into the three pass-type series.
 *
 * The server returns ONE ROW PER PROMOTION, each carrying a `type`:
 *
 *   { key: "132", label: "Membership", type: 0, points: [...] }
 *   { key: "133", label: "Membership", type: 2, points: [...] }
 *   { key: "134", label: "Membership", type: 1, points: [...] }
 *
 * A club can have several promotions of the same type, so rows are grouped by
 * `type` and summed per date. The row-level `key` and `label` are discarded:
 * once promotions are merged into a bucket a per-promotion name is meaningless,
 * and the sample data labels every row "Membership" regardless of its type.
 *
 * Always returns exactly three series, in PASS_TYPES order, even when a type has
 * no scans — otherwise the KPI tiles and the legend change shape between runs.
 */
export function aggregatePassTypes(rawSeries) {
    const buckets = {};
    PASS_TYPES.forEach(t => { buckets[t.key] = {}; });

    const unknownTypes = new Set();

    (rawSeries || []).forEach(s => {
        const key = REPORT_TYPE_TO_KEY[Number(s.type)];
        if (key === undefined) {
            // Don't guess. An unrecognised type means the server enum moved, and
            // quietly folding it into a bucket would corrupt a real number.
            unknownTypes.add(String(s.type));
            return;
        }
        (s.points || []).forEach(p => {
            buckets[key][p.date] = (buckets[key][p.date] || 0) + (Number(p.count) || 0);
        });
    });

    if (unknownTypes.size > 0) {
        console.warn(
            `[GeneralReport] scans-by-passtype returned unrecognised type value(s): `
            + `${[...unknownTypes].join(', ')}. Expected `
            + `${PASS_TYPES.map(t => `${t.reportType}=${t.key}`).join(', ')}. `
            + `Those rows were excluded from the chart.`,
        );
    }

    return PASS_TYPES.map(t => ({
        key: t.key,
        label: t.label,
        points: Object.keys(buckets[t.key]).map(date => ({ date, count: buckets[t.key][date] })),
    }));
}

/**
 * Rank by total descending, keep the top `max`, sum the rest into one "Other".
 *
 * Past ~7 series colour stops being able to tell them apart, so the tail folds
 * rather than getting invented hues. The full untruncated list is still returned
 * as `allSeries` for the table view.
 */
export function rankAndFold(series, days, max = MAX_SERIES) {
    const sorted = [...(series || [])].sort((a, b) => {
        if (b.total !== a.total) return b.total - a.total;
        return a.label.localeCompare(b.label); // stable when totals tie
    });

    if (sorted.length <= max) {
        return { series: sorted, allSeries: sorted, orderedKeys: sorted.map(s => s.key), foldedCount: 0 };
    }

    const kept = sorted.slice(0, max);
    const tail = sorted.slice(max);
    const points = days.map((date, i) => ({
        date,
        count: tail.reduce((sum, s) => sum + (s.points[i] ? s.points[i].count : 0), 0),
    }));
    const other = {
        key: OTHER_KEY,
        label: OTHER_LABEL,
        total: tail.reduce((sum, s) => sum + s.total, 0),
        points,
    };
    return {
        series: [...kept, other],
        allSeries: sorted,
        orderedKeys: [...kept.map(s => s.key), OTHER_KEY],
        foldedCount: tail.length,
    };
}

// Pivot series -> one row per day, which is the shape recharts wants.
export function toChartRows(series, days) {
    return days.map((date, i) => {
        const row = { date, label: formatDayLabel(date) };
        (series || []).forEach(s => { row[s.key] = s.points[i] ? s.points[i].count : 0; });
        return row;
    });
}

// ---------------------------------------------------------------------------
// Fetch
// ---------------------------------------------------------------------------

/**
 * Load one section and return it fully prepared for the chart.
 *
 * Resolves to { series, allSeries, orderedKeys, foldedCount, rows, days }.
 * Rejects with an Error, which the caller turns into the section's inline
 * "Failed - retry" state so one bad section never blocks the other two.
 */
export async function fetchSection(sectionId, params, UrlHelperObj) {
    const section = SECTIONS[sectionId];
    if (!section) throw new Error(`Unknown report section: ${sectionId}`);

    const days = enumerateDays(params.startDate, params.endDate);
    let raw = await realSection(section, params, UrlHelperObj);

    // Pass type is the one section the server doesn't return chart-ready: it
    // sends a row per promotion, which has to be folded into the three buckets.
    // Location and promotion pass straight through.
    if (sectionId === 'passType') raw = aggregatePassTypes(raw);

    const filled = zeroFill(raw, days);
    const folded = rankAndFold(filled, days);
    return { ...folded, rows: toChartRows(folded.series, days), days };
}

async function realSection(section, params, UrlHelperObj) {
    const body = {
        issuerId: params.issuerId,
        startDate: params.startDate,
        endDate: params.endDate,
    };
    const res = await axios.post(UrlHelperObj[section.urlMethod](), body);
    const data = res.data || {};
    if (Number(data.error) === 1) {
        throw new Error(data.message || 'Report could not be generated.');
    }
    return extractSeries(data, section);
}

/**
 * Pull the series array out of the response.
 *
 * The report APIs in this app do not agree on an envelope key - the Summary
 * Report plan says `results`, the Summary Report code reads `data.data`, and
 * this one asks for `series` (GENERAL_REPORT_PLAN.md §8.6 tracks reconciling
 * them). Rather than have a one-word mismatch look like "no data", accept any of
 * the three and say so in the console, so the disagreement is visible and
 * fixable instead of silent.
 */
function extractSeries(data, section) {
    const candidates = ['series', 'data', 'results'];
    const foundKey = candidates.find(k => Array.isArray(data[k]));

    if (!foundKey) {
        const keys = Object.keys(data).join(', ') || '(empty response)';
        throw new Error(
            `${section.id}: no series array in the response. Expected one of `
            + `${candidates.join(' / ')}, got: ${keys}`,
        );
    }
    if (foundKey !== 'series') {
        console.warn(
            `[GeneralReport] ${section.id}: server returned "${foundKey}" but the `
            + `contract specifies "series". Reading it anyway — worth reconciling. `
            + `See GENERAL_REPORT_PLAN.md §8.6.`,
        );
    }

    const rows = data[foundKey];
    // Surface a shape mismatch loudly too: a series with no points array almost
    // always means the server nested the daily numbers under a different name.
    const malformed = rows.find(r => !r || !Array.isArray(r.points));
    if (malformed) {
        throw new Error(
            `${section.id}: series entries are missing a "points" array. Got keys: `
            + `${Object.keys(malformed || {}).join(', ') || '(empty object)'}`,
        );
    }
    return rows;
}

